import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './ChatPage.css';
import momiboPeek from '../assets/momibo-peek.png';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const LARAVEL_API_URL = `${window.location.origin}/auth-api`;

const SUGGESTIONS = [
  '有給休暇の申請方法を教えて',
  'シフト変更の連絡はどうする？',
  '先月の売上を教えて',
];

function getCookie(name) {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
}

function ChatPage() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [isFloating] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading, isStreaming]);

  useEffect(() => {
    const laravelHeaders = {
      Accept: 'application/json',
      ...(getCookie('XSRF-TOKEN') ? { 'X-XSRF-TOKEN': getCookie('XSRF-TOKEN') } : {}),
    };
    axios
      .get(`${LARAVEL_API_URL}/user`, { withCredentials: true, headers: laravelHeaders })
      .then((res) => setCurrentUser(res.data))
      .catch(() => {});
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setFilePreview({ type: 'image', data: reader.result, name: file.name });
        };
        reader.readAsDataURL(file);
      } else {
        setFilePreview({ type: 'file', name: file.name, size: file.size });
      }
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
    setFilePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const renderMessageText = (text, lines) => {
    const parts = Array.isArray(lines) && lines.length > 0
      ? lines
      : String(text || '').split(/\r?\n/);
    return parts.map((line, i) => (
      <React.Fragment key={i}>
        {i > 0 && <br />}
        {line === '' ? '\u00A0' : line}
      </React.Fragment>
    ));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      sendMessage(e);
    }
  };

  const openInPopup = () => {
    const width = 420;
    const height = 650;
    const left = window.screen.width - width - 20;
    const top = window.screen.height - height - 80;

    window.open(
      '/app/chat',
      'MomiboChat',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=no,toolbar=no,menubar=no,location=no,status=no`
    );
  };

  const isPopup = window.opener !== null || window.name === 'MomiboChat';

  const sendMessage = async (e, presetQuestion) => {
    if (e?.preventDefault) e.preventDefault();
    const text = (presetQuestion ?? question).trim();
    if (!text && !selectedFile) return;

    const userMessage = {
      role: 'user',
      content: text,
      file: filePreview
    };
    setMessages((prev) => [...prev, userMessage]);
    setLoading(true);
    const currentQuestion = text;
    setQuestion('');
    const currentFile = selectedFile;
    removeFile();
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    try {
      let answerPayload = null;
      const laravelHeaders = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...(getCookie('XSRF-TOKEN') ? { 'X-XSRF-TOKEN': getCookie('XSRF-TOKEN') } : {}),
      };

      const hasAttachment = Boolean(currentFile);
      const looksLikeLogClient = /(やり取り|やりとり|問い合わせ|問合せ|会話ログ|過去ログ|過去の出来事|過去の対応|履歴|経緯|何があった)/.test(currentQuestion)
        || /(店[とのに]?.*過去|過去.*店)/.test(currentQuestion);

      // ファイル／画像添付時は Flask（Vision含む）へ直接送る
      if (!hasAttachment) {
        try {
          const logRes = await axios.post(
            `${LARAVEL_API_URL}/logs/ask`,
            { question: currentQuestion },
            { withCredentials: true, headers: laravelHeaders }
          );
          if (logRes.data?.handled && logRes.data?.answer) {
            answerPayload = {
              answer: logRes.data.answer,
              answer_lines: logRes.data.answer_lines || null,
              answer_html: logRes.data.answer_html || null,
              sources: [],
              skipStream: true,
            };
          }
        } catch (logError) {
          console.warn('Log ask skipped:', logError?.response?.status || logError.message);
        }

        if (!answerPayload && looksLikeLogClient) {
          try {
            const logRetry = await axios.post(
              `${LARAVEL_API_URL}/logs/ask`,
              { question: `${currentQuestion} のやり取り` },
              { withCredentials: true, headers: laravelHeaders }
            );
            if (logRetry.data?.handled && logRetry.data?.answer) {
              answerPayload = {
                answer: logRetry.data.answer,
                answer_lines: logRetry.data.answer_lines || null,
                answer_html: logRetry.data.answer_html || null,
                sources: [],
                skipStream: true,
              };
            }
          } catch (logRetryError) {
            console.warn('Log ask retry skipped:', logRetryError?.response?.status || logRetryError.message);
          }
        }

        if (!answerPayload) {
          try {
            const knowledgeRes = await axios.post(
              `${LARAVEL_API_URL}/knowledge/ask`,
              { question: currentQuestion },
              { withCredentials: true, headers: laravelHeaders }
            );
            if (knowledgeRes.data?.handled && knowledgeRes.data?.answer) {
              answerPayload = {
                answer: knowledgeRes.data.answer,
                sources: [],
                images: knowledgeRes.data.images || [],
              };
            }
          } catch (knowledgeError) {
            console.warn('Knowledge ask skipped:', knowledgeError?.response?.status || knowledgeError.message);
          }
        }

        if (!answerPayload && !looksLikeLogClient) {
          try {
            const kpiRes = await axios.post(
              `${LARAVEL_API_URL}/kpi/ask`,
              { question: currentQuestion },
              { withCredentials: true, headers: laravelHeaders }
            );
            if (kpiRes.data?.handled && kpiRes.data?.answer) {
              answerPayload = { answer: kpiRes.data.answer, sources: [] };
            }
          } catch (kpiError) {
            console.warn('KPI ask skipped:', kpiError?.response?.status || kpiError.message);
          }
        }
      }

      if (!answerPayload) {
        const formData = new FormData();
        formData.append('question', currentQuestion || '');
        if (currentUser?.id) {
          formData.append('user_id', String(currentUser.id));
        }
        if (currentUser?.name) {
          formData.append('user_name', currentUser.name);
        }
        if (currentUser?.shop_name) {
          formData.append('shop_name', currentUser.shop_name);
        }
        if (currentFile) {
          formData.append('file', currentFile);
        }

        const response = await axios.post(`${API_URL}/chat`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        answerPayload = response.data;
      }

      setLoading(false);

      const aiMessage = {
        role: 'assistant',
        content: answerPayload.answer,
        lines: answerPayload.answer_lines || null,
        html: answerPayload.answer_html || null,
        sources: answerPayload.sources,
        chart: answerPayload.chart,
        table: answerPayload.table,
        extracted_images: answerPayload.extracted_images,
        images: answerPayload.images
      };
      setMessages((prev) => [...prev, aiMessage]);
      setStreamingText('');
      setIsStreaming(false);

      const replyText = String(answerPayload?.answer || '');
      const unansweredMarkers = /\[UNANSWERED\]|担当者より折り返し|本部担当者より折り返し|本部で確認|本部に確認します|お答えできる情報が見つかりません|該当する情報が見つかりません|見つかりませんでした|回答できません/;
      if (unansweredMarkers.test(replyText)) {
        try {
          await axios.post(
            `${LARAVEL_API_URL}/unanswered`,
            { question: currentQuestion, bot_reply: replyText },
            { withCredentials: true, headers: laravelHeaders }
          );
        } catch (captureError) {
          console.warn('Unanswered capture skipped:', captureError?.response?.status || captureError.message);
        }
      }

    } catch (error) {
      console.error('メッセージ送信エラー:', error);
      setLoading(false);
      const errorContent = error.response?.data?.error || 'エラーが発生しました。もう一度お試しください。';
      setMessages((prev) => [...prev, { role: 'assistant', content: errorContent, isError: true }]);
    }
  };

  const onTextareaInput = (e) => {
    setQuestion(e.target.value);
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 140)}px`;
  };

  const showEmpty = messages.length === 0 && !loading;

  return (
    <article className={`chat-page ${isFloating ? 'floating-mode' : ''} ${isMinimized ? 'minimized' : ''} ${isPopup ? 'popup-mode' : ''}`}>
      <header className="chat-header">
        <div className="header-left">
          {!isPopup && (
            <button type="button" className="home-btn" onClick={() => navigate('/')} aria-label="ホームへ">
              <span aria-hidden="true">←</span>
              <span className="home-btn-label">ホーム</span>
            </button>
          )}
          <div className="brand-lockup">
            <div className="brand-text">
              <div className="header-logo">MOMIBO</div>
              <div className="header-sub">本部サポート</div>
            </div>
          </div>
        </div>
        <div className="header-actions">
          {!isPopup && (
            <a className="hq-home-link" href="/auth/dashboard">
              本部ホーム
            </a>
          )}
          {!isPopup ? (
            <button
              type="button"
              className="floating-toggle-btn"
              onClick={openInPopup}
              title="別ウィンドウで開く"
            >
              別窓
            </button>
          ) : (
            <button
              type="button"
              className="floating-toggle-btn"
              onClick={() => window.close()}
              title="閉じる"
            >
              閉じる
            </button>
          )}
        </div>
      </header>

      {isFloating && !isMinimized && (
        <button type="button" className="minimize-btn" onClick={() => setIsMinimized(true)} title="最小化">─</button>
      )}

      {isFloating && isMinimized && (
        <button type="button" className="chat-bubble" onClick={() => setIsMinimized(false)} aria-label="チャットを開く">
          M
        </button>
      )}

      <div className="chat-shell">
        <div className="chat-panel">
          <div className="chat-data-box">
            {showEmpty && (
              <div className="empty-state">
                <div className="empty-copy">
                  <h1 className="empty-title">なにかお手伝いできますか？</h1>
                  <p className="empty-lead">マニュアル・店舗の数字・過去の対応など、そのまま聞いてください。</p>
                  <div className="suggestion-row">
                    {SUGGESTIONS.map((s) => (
                      <button
                        key={s}
                        type="button"
                        className="suggestion-chip"
                        disabled={loading}
                        onClick={() => sendMessage(null, s)}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="empty-peek" aria-hidden="true">
                  <img src={momiboPeek} alt="" className="empty-peek-img" />
                </div>
              </div>
            )}

            {messages.map((msg, index) => (
              <div key={index} className={msg.role === 'user' ? 'qry-area' : 'res-area'}>
                {msg.role === 'user' ? (
                  <div className="direct-qry-text-box">
                    <div className="direct-qry-content">
                      {msg.file && msg.file.type === 'image' && (
                        <img src={msg.file.data} alt="uploaded" className="message-image" />
                      )}
                      {msg.file && msg.file.type === 'file' && (
                        <div className="message-file">📎 {msg.file.name}</div>
                      )}
                      {msg.content && <div className="direct-qry-text">{renderMessageText(msg.content)}</div>}
                    </div>
                  </div>
                ) : (
                  <div className="direct-res-text-box">
                    <div className="direct-res-content">
                      <div className="direct-res-name">MOMIBO</div>
                      {msg.html ? (
                        <div className={`direct-res-text log-answer ${msg.isError ? 'is-error' : ''}`} dangerouslySetInnerHTML={{ __html: msg.html }} />
                      ) : (
                        <div className={`direct-res-text ${msg.isError ? 'is-error' : ''}`}>{renderMessageText(msg.content, msg.lines)}</div>
                      )}
                      {msg.chart && <img src={msg.chart} alt="chart" className="response-chart" />}
                      {msg.table && <div className="response-table" dangerouslySetInnerHTML={{ __html: msg.table }} />}
                      {msg.images && msg.images.length > 0 && (
                        <div className="found-images-container">
                          <div className="found-images-header">説明画像</div>
                          <div className="found-images-grid">
                            {msg.images.map((img, idx) => (
                              <div key={idx} className="found-image-item">
                                <img src={img.data} alt={img.filename} className="found-image" />
                                <div className="found-image-name">{img.filename}</div>
                                <a href={img.data} download={img.filename} className="download-image-btn">保存</a>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {msg.extracted_images && msg.extracted_images.length > 0 && (
                        <div className="extracted-images-container">
                          <div className="extracted-images-header">抽出画像</div>
                          <div className="extracted-images-grid">
                            {msg.extracted_images.map((img, idx) => (
                              <div key={idx} className="extracted-image-item">
                                <img src={img.data} alt={`extracted-${idx}`} className="extracted-image" />
                                <a
                                  href={img.data}
                                  download={`image-page${img.page}-${idx + 1}.${img.format}`}
                                  className="download-image-btn"
                                >
                                  保存
                                </a>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}

            {loading && (
              <div className="res-area">
                <div className="direct-res-text-box">
                  <div className="direct-res-content">
                    <div className="direct-res-name">MOMIBO</div>
                    <div className="direct-res-text loading">
                      <span className="typing-dots" aria-hidden="true"><i /><i /><i /></span>
                      調べています…
                    </div>
                  </div>
                </div>
              </div>
            )}

            {isStreaming && (
              <div className="res-area">
                <div className="direct-res-text-box">
                  <div className="direct-res-content">
                    <div className="direct-res-name">MOMIBO</div>
                    <div className="direct-res-text streaming">{renderMessageText(streamingText)}<span className="cursor">▋</span></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="question-area">
            {filePreview && (
              <div className="file-preview-container">
                {filePreview.type === 'image' ? (
                  <img src={filePreview.data} alt="preview" className="file-preview-image" />
                ) : (
                  <div className="file-preview-doc">📎 {filePreview.name}</div>
                )}
                <button type="button" className="remove-file-btn" onClick={removeFile} aria-label="添付を削除">✕</button>
              </div>
            )}
            <form className="composer" onSubmit={sendMessage}>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept="image/*,.pdf,.doc,.docx,.txt"
                style={{ display: 'none' }}
              />
              <button
                type="button"
                className="attach-button"
                onClick={() => fileInputRef.current?.click()}
                disabled={loading}
                title="ファイルを添付"
                aria-label="ファイルを添付"
              >
                ＋
              </button>
              <textarea
                ref={textareaRef}
                className="textarea-t"
                rows="1"
                value={question}
                onChange={onTextareaInput}
                onKeyDown={handleKeyDown}
                placeholder="質問を入力（Enterで送信）"
                disabled={loading}
              />
              <button
                className="submit-button"
                type="submit"
                disabled={loading || (!question.trim() && !selectedFile)}
              >
                送信
              </button>
            </form>
            <p className="composer-hint">Shift+Enter で改行</p>
          </div>
        </div>
      </div>
    </article>
  );
}

export default ChatPage;
